import { Link } from "react-router-dom";
import { DELIVERS, STAGES } from "@/content/journey";
import { CAPABILITIES, CAP_IDS } from "@/content/software";
import { PageIntro } from "@/components/site/PageIntro";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { ActionLink, Container, Eyebrow, Mono, SectionTitle, TextLink } from "@/components/site/primitives";

const CapList = () => (
  <ul className="mt-6 grid gap-2 sm:grid-cols-2">
    {CAP_IDS.map((id) => {
      const c = CAPABILITIES[id];
      return (
        <li key={id}>
          <Link to={c.path} state={{ via: "software" }} data-testid={`enterprise-cap-${id}`}
            className="flex h-full flex-col gap-2 border border-o-line bg-o-surface p-4 transition-colors hover:border-o-accent">
            <span aria-hidden className="h-1 w-8" style={{ background: c.hue }} />
            <span className="font-display font-bold">{c.name}</span>
            <span className="text-xs text-o-dim">{c.family === "ordo" ? "Ordo family" : "Independent"} · {c.responsibility}</span>
          </Link>
        </li>
      );
    })}
  </ul>
);

const Entrances = () => (
  <section className="border-b border-o-line" data-testid="enterprise-entrances">
    <Container className="grid gap-4 py-16 lg:grid-cols-2 lg:py-24">
      <article className="flex flex-col justify-between gap-10 border-2 border-o-accent bg-o-surface p-6 sm:p-10" data-testid="enterprise-entrance-diagnostic">
        <div>
          <Mono accent>Entrance 01 · Start with a problem</Mono>
          <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">Ordo Diagnostic</h2>
          <p className="mt-4 max-w-md leading-relaxed text-o-dim">Tell us one problem your business keeps dealing with. Ordo Intelligence investigates what needs to change and recommends an appropriate response.</p>
          <ol className="mt-6 flex flex-wrap gap-2">
            {STAGES.map((s) => (
              <li key={s.id} className="border border-o-line px-2.5 py-1 font-mono text-[10.5px] uppercase tracking-[0.14em] text-o-dim">{s.label}</li>
            ))}
          </ol>
        </div>
        <ActionLink to="/enterprise/diagnostic" testid="enterprise-diagnostic-btn" className="self-start">Explore Ordo Diagnostic</ActionLink>
      </article>
      <article className="flex flex-col justify-between gap-10 border border-o-line bg-o-raise p-6 sm:p-10" data-testid="enterprise-entrance-software">
        <div>
          <Mono accent>Entrance 02 · Go directly to software</Mono>
          <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">Applied software</h2>
          <p className="mt-4 max-w-md leading-relaxed text-o-dim">Four capabilities, each with its own responsibility. Organizations adopt what their situation calls for.</p>
          <CapList />
        </div>
        <ActionLink to="/enterprise/software" variant="line" testid="enterprise-software-btn" className="self-start">See all four in one view</ActionLink>
      </article>
    </Container>
  </section>
);

const Deliverables = () => (
  <section className="border-b border-o-line" data-testid="enterprise-deliverables">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-4">
        <Eyebrow index="02">What the operation can deliver</Eyebrow>
        <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">From diagnosis to continuing support.</SectionTitle>
        <p className="mt-4 text-o-dim">Each is delivered only where the situation warrants it.</p>
      </div>
      <ol className="border-t border-o-line lg:col-span-8">
        {DELIVERS.map((d, i) => (
          <li key={d.label} className="grid gap-2 border-b border-o-line py-5 sm:grid-cols-12 sm:items-baseline sm:gap-4">
            <Mono accent className="sm:col-span-1">0{i + 1}</Mono>
            <span className="font-display text-xl font-bold sm:col-span-4">{d.label}</span>
            <span className="text-o-dim sm:col-span-5">{d.text}</span>
            <Mono className="sm:col-span-2 sm:text-right">{d.stage}</Mono>
          </li>
        ))}
      </ol>
    </Container>
  </section>
);

const Origin = () => (
  <section className="border-b border-o-line" data-testid="enterprise-origin">
    <Container className="py-16 lg:py-20">
      <div className="grid gap-6 border border-o-line bg-o-surface p-6 sm:p-10 md:grid-cols-12">
        <div className="md:col-span-8">
          <Mono accent>Where this operation sits</Mono>
          <p className="mt-3 text-lg leading-relaxed">
            Diagnostics & Software is customer-originated: it begins with an organization's situation. Its sibling operation, Research & Products, is company-originated. Both draw on Human Structure and General Ordo.
          </p>
        </div>
        <div className="flex flex-col gap-3 md:col-span-3 md:col-start-10 md:justify-end">
          <TextLink to="/research" testid="enterprise-research-link">Research & Products</TextLink>
          <TextLink to="/ordo" testid="enterprise-ordo-link">General Ordo</TextLink>
        </div>
      </div>
    </Container>
  </section>
);

export default function EnterprisePage() {
  return (
    <>
      <PageIntro routeKey="enterprise" group="enterprise" index="DS" eyebrow="Diagnostics & Software · Customer-originated"
        title="Begins with your actual organizational situation."
        lede="Diagnostics & Software investigates what needs to change in an organization, determines an appropriate response, and can deliver fitted software, architectural advisory, implementation, and continuing support."
        aside={<Placeholder label="Diagnostics & Software illustration" note="An organization's working situation" ratio="4 / 3" testid="placeholder-enterprise" />} />
      <Entrances />
      <Deliverables />
      <Origin />
      <EngageBand path="business" from="/enterprise" title="Bring a problem, or ask about a capability."
        text="Start a business inquiry. During Phase 1 the inquiry form is an inactive preview." />
    </>
  );
}
